import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, Linkedin, Twitter, Facebook } from 'lucide-react';
import apiClient from '../config/axios';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await apiClient.post('/api/contact', formData);
      setStatus({
        type: 'success',
        message: response.data.message || 'Thank you for contacting us! We will get back to you soon.'
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to submit form. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Contact Us</h1>
          <p>Get in touch with us for your embedded systems needs</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h2>Send Us a Message</h2>
              <p>Fill out the form below and we'll get back to you as soon as possible.</p>

              {status.message && (
                <div className={`alert alert-${status.type}`}>
                  {status.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91-XXXXXXXXXX"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject">Subject</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                    >
                      <option value="">Select a subject</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Firmware Development">Firmware Development</option>
                      <option value="Hardware Design">Hardware Design</option>
                      <option value="PCB Design">PCB Design</option>
                      <option value="IoT Solutions">IoT Solutions</option>
                      <option value="Career Inquiry">Career Inquiry</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your project or inquiry..."
                    rows="6"
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={18} />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info-wrapper">
              <div className="contact-info-card">
                <h3>Contact Information</h3>
                <p>Reach out to us through any of the following channels.</p>

                <div className="contact-details">
                  <div className="contact-item">
                    <div className="contact-icon">
                      <MapPin size={24} />
                    </div>
                    <div className="contact-text">
                      <h4>Our Office</h4>
                      <p>Tech Park, HITEC City<br />Hyderabad, Telangana 500081<br />India</p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon">
                      <Phone size={24} />
                    </div>
                    <div className="contact-text">
                      <h4>Phone</h4>
                      <p><a href="tel:+919985784340">+91-9985784340</a></p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon">
                      <Mail size={24} />
                    </div>
                    <div className="contact-text">
                      <h4>Email</h4>
                      <p><a href="mailto:info@signatureesys.com">info@signatureesys.com</a></p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon">
                      <Clock size={24} />
                    </div>
                    <div className="contact-text">
                      <h4>Business Hours</h4>
                      <p>Monday - Friday: 9:00 AM - 6:00 PM<br />Saturday: 9:00 AM - 1:00 PM</p>
                    </div>
                  </div>
                </div>

                <div className="social-section">
                  <h4>Follow Us</h4>
                  <div className="social-links">
                    <a href="https://linkedin.com/company/signatureesys" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                      <Linkedin size={22} />
                    </a>
                    <a href="https://twitter.com/signatureesys" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                      <Twitter size={22} />
                    </a>
                    <a href="https://facebook.com/signatureesys" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                      <Facebook size={22} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="map-placeholder">
          <MapPin size={48} />
          <h3>Hyderabad, Telangana, India</h3>
          <p>Tech Park, HITEC City</p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
